/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, Page } from 'componentregistry/registry/ComponentRegistry';
import { getManager } from 'componentregistry/registry/Manager';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSwitchCase';

const ActionSwitchCaseManager: ActionManagerFn<'ActionSwitchCase'> = (
  platform: Platform,
): ActionManager<'ActionSwitchCase'> => {
  return {
    ...baseActionManager(platform, 'ActionSwitchCase'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      getManager(childType).managerType === 'Action' &&
      childType !== 'ActionSwitchCase',
    isAllowedInsideParent: (page: Page, parent: AnyItem) =>
      parent.type === 'ActionSwitch',
    props: {
      ...baseActionProps,
      caseValue: {
        label: 'Case',
        type: 'string',
        helpText:
          'Provide a constant string value (without quotes) for this case to match the above switch condition.',
      },
    },
    isPropertyVisible(prop): boolean {
      switch (prop) {
        case 'disableCondition':
          return false;
        default:
          return true;
      }
    },
    name: 'Case',
    getDisplayText: (item) => item.props.caseValue,
    description: `**Overview**<br/>
The Switch Case action is a structural component that defines a specific branch of logic within a parent "Switch" block. It functions as the "If the value equals X..." statement. It cannot exist independently; it serves strictly as a container for other actions that should only execute when the parent Switch's expression matches this Case's defined value.<br/><br/>
When the parent ActionSwitch evaluates its expression, it checks each child Case in order. If it finds a Case where the caseValue matches, it enters this container and runs the actions inside it.

**Key Features**<br/>
* **Logic Branching:** Represents a single unique path in a multi-option workflow.
* **Strict Parent Scope:** Enforces usage validity via isAllowedInsideParent, ensuring it only functions as a direct child of an ActionSwitch.
* **Action Container:** isChildAllowed is configured to accept any standard Action (e.g., Save, Show Message, Go To Page) as children, except for another ActionSwitchCase (preventing invalid nesting).
* **Simplified Logic:** Unlike standard "Conditions" which use complex expressions, this component uses a simple direct value match, making the flow easier to read.

**Properties**<br/>
* **Case (caseValue):** A string property.
  * *Function:* The constant value to match against the parent Switch's expression.
  * *Format:* A plain string (e.g., Approved or Rejected). Do not wrap in quotes.
  * *Behavior:* If Switch Expression === Case Value, the actions inside this block are executed.

**Use Cases**<br/>
* **Status Handling:** Inside a Switch checking OrderStatus:
  * *Case "New":* Runs actions to enable the "Edit" button.
  * *Case "Shipped":* Runs actions to show the "Track Package" link.
* **Navigation Routing:** Inside a Switch checking UserRole:
  * *Case "Admin":* Navigates to the Settings page.
  * *Case "Guest":* Shows a "Please Login" message.`,
  };
};

export default ActionSwitchCaseManager;
